import React from "react";
import { Box, Grid } from "@mui/material";
import { useState, useEffect } from "react";
import {
    ModalPhoneInputWithDialCode,
    StandardModal,
    StandardModalActionButton,
    StandardModalErrorText,
    StandardModalFormGrid,
    StandardModalFormGroup,
    StandardModalHelperText,
    StandardModalInput,
    StandardModalLabel,
    StandardModalSelect,
    portalColors
} from "src/components";
import styled, { css } from "styled-components";
import pluralize from "pluralize";
import RequestDocumentsSection from "./RequestDocumentsSection";
import ConvertIcon from "src/img/convert.svg";

const Required = styled.span`
    color: ${portalColors.danger};
    font-style: italic;
    font-weight: 400;
    
    ::before {
        content: "(required)";
        font-size: 0.8rem;
    }
`;

const RadioButton = styled.button`
    position: relative;
    width: min(100%, 15.625rem);
    height: 2.5rem;
    border: 1px solid ${p => p.active ? portalColors.text : portalColors.inputBorder};
    box-shadow: ${portalColors.inputShadow};
    border-radius: var(--calendar-radius-control, 8px);
    text-align: center;
    font-family: Manrope, Helvetica, sans-serif;
    font-weight: 700;
    color: ${p => p.active ? "var(--calendar-bg, #FFFFFF)" : portalColors.text};
    background: ${p => p.active ? portalColors.text : portalColors.surfaceSoft};
    cursor: pointer;
    transition: background 0.16s ease, border-color 0.16s ease, color 0.16s ease;

    ${(props) =>
        props.active &&
        css`
          background-color: ${portalColors.text};
          color: var(--calendar-bg, #FFFFFF) !important;
          font-weight: 700;
    `}
    ${(props) =>
        props.disabled &&
        css`
          pointer-events: none;
          color: ${portalColors.textSoft};
    `}
`;

const StyledInput = styled(StandardModalInput)`
    width: 100%;
    max-width: none;
    height: 42px;
    text-align: left;
    box-shadow: ${portalColors.inputShadow};
    border-color: ${portalColors.inputBorder};
    padding: 0 12px;

    ::placeholder {
        text-align: left;
        color: ${portalColors.textSoft};
    }
`;

const TagsAssignmentSection = styled.div`
    display: grid;
    gap: 0.9rem;
    padding-top: 0.5rem;
`;

const TagsAssignmentBody = styled.div`
    display: grid;
    grid-template-columns: minmax(12rem, 16rem) minmax(0, 1fr);
    gap: 1rem;
    align-items: stretch;

    @media (max-width: 760px) {
        grid-template-columns: 1fr;
    }
`;

const TagSelect = styled(StandardModalSelect)`
    width: 100%;
    max-width: none;
    height: 42px;
    box-shadow: ${portalColors.inputShadow};
    border-color: ${portalColors.inputBorder};
    background-color: ${portalColors.surface};
`;

const SelectedTagsBox = styled.div`
    min-height: 7rem;
    display: flex;
    align-content: flex-start;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0.75rem;
    border: 1px solid ${portalColors.inputBorder};
    border-radius: 8px;
    background: ${portalColors.surface};
    box-shadow: ${portalColors.inputShadow};
    box-sizing: border-box;
`;

const SelectedTagPill = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    max-width: 100%;
    min-height: 2rem;
    padding: 0.35rem 0.7rem;
    border: 0;
    border-radius: 7px;
    background: var(--calendar-dark-ink, #171A22);
    color: var(--calendar-dark-control-text, #FFFFFF);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
    cursor: pointer;
    overflow-wrap: anywhere;
`;

const EmptySelectedTags = styled.span`
    color: ${portalColors.textSoft};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 600;
`;

const TagsLeadText = styled.div`
    display: grid;
    gap: 0.45rem;
`;

const UserFormContent = styled(Box)`
    width: 100%;
    max-width: none;
    display: grid;
    gap: 1rem;
`;

const PhoneInputWithDialCode = (props) => {
    const { countryCode, onMount, onChange, value, dialCode } = props;
    return (
        <ModalPhoneInputWithDialCode
            countryCode={countryCode}
            dialCode={dialCode}
            value={value}
            onMount={onMount}
            onChange={onChange}
        />
    );
};

const Circle = styled.div`
    position: absolute;
    right: 0;
    bottom: 9px;
    margin-right: 10px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: #76C00D;
    display: flex;
    justify-content: center;
    align-items: center;
    visibility: hidden;

    ${(props) =>
        props.active &&
        css`
            visibility: visible;
    `}
    ${(props) =>
        props.disabled &&
        css`
          pointer-events: none;
    `}
`;

const CheckMark = styled.span`
    color: white;
    font-weight: 700;
    font-size: 10px;
`;

const ConvertDialogContent = styled.div`
    width: 100%;
    max-width: calc(100vw - 48px);
    min-height: 330px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 42px 70px 36px;
    text-align: center;
    box-sizing: border-box;

    @media (max-width: 640px) {
        padding: 2rem 1.25rem;
    }
`;

const ConvertTitle = styled.h2`
    margin: 10px 0 24px;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 2rem;
    font-weight: 700;
    color: ${portalColors.text};
`;

const ConvertMessage = styled.p`
    width: 520px;
    max-width: 100%;
    margin: 0 0 50px;
    font: normal normal normal 16px/19px Manrope;
    font-size: 0.875rem;
    line-height: 1.2;
    color: ${portalColors.mutedText};
`;

const ConvertActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
`;

const DiscardDialogContent = styled.div`
    width: 100%;
    max-width: calc(100vw - 48px);
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 32px 40px 30px;
    text-align: center;
    box-sizing: border-box;

    @media (max-width: 640px) {
        padding: 2rem 1.25rem;
    }
`;

const DiscardTitle = styled.h2`
    margin: 0 0 16px;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 1.5rem;
    font-weight: 700;
    color: ${portalColors.text};
`;

const DiscardMessage = styled.p`
    margin: 0 0 28px;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    line-height: 1.45;
    color: ${portalColors.mutedText};
`;

const UserDetailsForm = (props) => {
    const { 
        showDialog, 
        handleClose, 
        user, 
        createNewUser, 
        removeUsers, 
        updateEmployee, 
        updateUserType,
        countryCode, 
        customNames, 
        documentTypes,
        userTags,
    } = props;
    const [formData, setFormData] = useState(props.user);
    const [errorList, setErrorList] = useState([]);
    const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
    const [isLoadingDelete, setIsLoadingDelete] = useState(false);
    const [isLoadingConvert, setIsLoadingConvert] = useState(false);
    const [pendingUserType, setPendingUserType] = useState(null);
    const [pendingDiscardUserType, setPendingDiscardUserType] = useState(null);

    const [dialCode, setDialCode] = useState("");

    const isFormCreate = user.formType === "add";
    const singularEmployeeName = pluralize.singular(customNames.employees);
    const singularClientName = pluralize.singular(customNames.clients);
    const currentUserTypeName = user.isClient ? singularClientName : singularEmployeeName;
    const nextUserTypeName = pendingUserType ? singularClientName : singularEmployeeName;
    const showConvertDialog = pendingUserType !== null;

    const handleInputChange = (event, key) => {
        const updatedFormData = { ...formData, [key]: event.target.value };
        setFormData(updatedFormData);
    };

    const emptyPhoneFieldWhenEmptyString = (phone, country) => {
        if (formData.phoneNumber !== "" && !country)
            return;

        if (country.dialCode !== phone)
            return;

        const updatedFormData = { ...formData, phoneNumber: "" };
        setFormData(updatedFormData);
    };

    const changePhoneNumber = (phone, country) => {
        setDialCode(country.dialCode);

        const updatedFormData = { ...formData, phoneNumber: phone };
        setFormData(updatedFormData);
    };

    const handlePhoneNumber = (phone, country) => {
        (async () => {
            await Promise.resolve(changePhoneNumber(phone, country));
            emptyPhoneFieldWhenEmptyString(phone, country);
        })();
    };

    const onPhoneInputMounted = (value, country) => {
        if (country.dialCode) setDialCode(country.dialCode);

        const updatedFormData = { ...formData, phoneNumber: value.slice(country.dialCode.length) };
        setFormData(updatedFormData);
    };

    const handleFormSubmit = async () => {
        setErrorList([]);
        setIsLoadingSubmit(true);

        let requiredFields = ["firstName", "lastName", "email"];
        if (formData.isClient)
            requiredFields = ["firstName", "email"];

        let requiredFieldsHasValue = true;
        requiredFields.forEach(field => {
            if (!formData[field].trim()) {
                requiredFieldsHasValue = false;
            }
        });

        if (!requiredFieldsHasValue) {
            setIsLoadingSubmit(false);
            return setErrorList((prevErrorList) => [...prevErrorList, "Please fill all required fields."]);
        }

        if (!validateEmail(formData.email)) {
            setIsLoadingSubmit(false);
            return setErrorList((prevErrorList) => [...prevErrorList, "Invalid email format."]);
        }

        let formRequest = {
            ...formData,
            selectedDocuments: formData.selectedDocuments || [],
            selectedTags: formData.selectedTags || []
        };
        formRequest = {
            ...formRequest,
            phoneNumber: formRequest.phoneNumber.length > 0 ? "+" + dialCode + formRequest.phoneNumber : ""
        };

        try {
            if (isFormCreate) {
                await createNewUser(formRequest);
            } else {
                await updateEmployee(formRequest);
            }
        } catch (error) {
            console.error("An error occurred during form submission:", error);
        } finally {
            setIsLoadingSubmit(false);
        }
    };

    const handleDeleteUser = async () => {
        setIsLoadingDelete(true);
        try {
            await removeUsers([user.uuid]);
            handleClose();
        } catch (error) {
            console.error("An error occurred during user deletion:", error);
        } finally {
            setIsLoadingDelete(false);
        }
    };

    const getComparableTextFields = (data) => ({
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        email: data.email || "",
        department: data.department || "",
        employmentPosition: data.employmentPosition || ""
    });

    const hasUnsavedChanges = () => {
        const currentValues = getComparableTextFields(formData);
        const savedValues = getComparableTextFields(user);

        const textChanged = Object.keys(currentValues).some(
            key => currentValues[key] !== savedValues[key]
        );

        const currentDocuments = [...(formData.selectedDocuments || [])].sort();
        const savedDocuments = [...(user.selectedDocuments || [])].sort();
        const currentTags = [...(formData.selectedTags || [])].sort();
        const savedTags = [...(user.selectedTags || [])].sort();

        const documentsChanged =
        JSON.stringify(currentDocuments) !== JSON.stringify(savedDocuments);
        const tagsChanged = JSON.stringify(currentTags) !== JSON.stringify(savedTags);

        return textChanged || documentsChanged || tagsChanged;
    };

    const resetFormData = (nextIsClient = user.isClient) => {
        setFormData({
            ...user,
            isClient: nextIsClient,
            selectedDocuments: user?.selectedDocuments || [],
            selectedTags: user?.selectedTags || [],
            userTags: user?.userTags || []
        });
    };

    const handleTagSelect = (event) => {
        const tagUuid = event.target.value;
        if (!tagUuid) {
            return;
        }

        const selectedTags = formData.selectedTags || [];
        if (selectedTags.includes(tagUuid)) {
            return;
        }

        setFormData({
            ...formData,
            selectedTags: [...selectedTags, tagUuid]
        });
    };

    const removeSelectedTag = (tagUuid) => {
        const selectedTags = formData.selectedTags || [];
        setFormData({
            ...formData,
            selectedTags: selectedTags.filter(uuid => uuid !== tagUuid)
        });
    };

    const handleUserTypeSelection = (value) => {
        const nextIsClient = Boolean(value);
        if (Boolean(formData.isClient) === nextIsClient) {
            return;
        }

        if (!isFormCreate && hasUnsavedChanges()) {
            setPendingDiscardUserType(nextIsClient);
            return;
        }

        if (isFormCreate) {
            setFormData({ ...formData, isClient: nextIsClient });
            return;
        }

        resetFormData(user.isClient);
        setPendingUserType(nextIsClient);
    };

    const handleConfirmDiscardTextEdits = () => {
        resetFormData(user.isClient);
        setPendingUserType(pendingDiscardUserType);
        setPendingDiscardUserType(null);
    };

    const handleCloseDiscardDialog = () => {
        setPendingDiscardUserType(null);
    };

    const handleConfirmUserTypeChange = async () => {
        setErrorList([]);
        setIsLoadingConvert(true);

        try {
            await updateUserType(user.uuid, Boolean(user.isClient), Boolean(pendingUserType));
        } catch (error) {
            console.error("An error occurred during user type conversion:", error);
        } finally {
            setIsLoadingConvert(false);
        }
    };

    const handleCloseConvertDialog = () => {
        if (isLoadingConvert) {
            return;
        }
        setPendingUserType(null);
    };

    const validateEmail = (email) => {
        return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email);
    };

    const renderErrors = errorList.map((error, index) => {
        return errorList.length ? <StandardModalErrorText key={index}>{error}</StandardModalErrorText>
            : "";
    });

    // prevents console warns when one of the input values are null
    const filterInputValue = (value) => [null].includes(value) ? "" : value;

    const selectedTagUuids = formData.selectedTags || [];
    const selectedTagSource = [...(userTags || []), ...(formData.userTags || [])];
    const selectedTagObjects = selectedTagUuids
        .map(tagUuid => selectedTagSource.find(tag => tag.uuid === tagUuid))
        .filter(Boolean);
    const availableTags = (userTags || []).filter(tag => !selectedTagUuids.includes(tag.uuid));

    useEffect(() => {
        resetFormData();
        setPendingUserType(null);
        setPendingDiscardUserType(null);
    }, [user]);

    useEffect(() => {
        setErrorList(props.errorList);
    }, [props.errorList]);

    return (
        <>
            <StandardModal
                open={showDialog && !showConvertDialog}
                onClose={handleClose}
                title={isFormCreate ? "Add New User" : `Edit ${currentUserTypeName} User`}
                description={isFormCreate ? `Please select whether this individual is an ${singularEmployeeName} or a ${singularClientName} of your business, and provide the relevant details accordingly.` : undefined}
                widthMode="compactWide"
                bodyGap="1rem"
                footer={(
                    <div style={{ display: "flex", justifyContent: "space-between", width: "100%", gap: "0.75rem", flexWrap: "wrap" }}>
                        <div>
                            {!isFormCreate &&
                                <StandardModalActionButton
                                    $variant="danger"
                                    style={{ opacity: isLoadingDelete && "0.6" }}
                                    width="9.375rem"
                                    height="2.3125rem"
                                    onClick={() => handleDeleteUser()}
                                >
                                    {!isLoadingDelete ? "Delete User" : "Deleting user..."}
                                </StandardModalActionButton>
                            }
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", flexWrap: "wrap", marginLeft: "auto", visibility: isLoadingDelete ? "hidden" : "visible" }}>
                            <StandardModalActionButton
                                width="140px"
                                onClick={handleClose}
                            >
                                Cancel
                            </StandardModalActionButton>
                            <StandardModalActionButton
                                $variant="primary"
                                width="140px"
                                style={{ opacity: isLoadingSubmit && "0.6" }}
                                type="submit"
                                onClick={(e) => handleFormSubmit(e)}
                                disabled={isLoadingSubmit}

                            >
                                {isLoadingSubmit && "Saving..."}
                                {!isLoadingSubmit && (isFormCreate ? "Add User" : "Save")}
                            </StandardModalActionButton>
                        </div>
                    </div>
                )}
            >
                <UserFormContent>
                    <Grid container spacing={2}>
                        <Grid item sm={12} md={12}>
                            <StandardModalLabel>{isFormCreate ? "Select User Type" : "User Type"}</StandardModalLabel>
                        </Grid>
                        <Grid item sm={12} md={4}>
                            <div className="radio">
                                <input type="radio" name="userType" value="employee" style={{ position: "fixed", visibility: "hidden" }} />
                                <RadioButton active={!formData.isClient}
                                    onClick={() => handleUserTypeSelection(false)}
                                >
                                    {singularEmployeeName}
                                    <Circle active={!formData.isClient}><CheckMark>&#x2713;</CheckMark></Circle>
                                </RadioButton>
                            </div>
                        </Grid>

                        <Grid item sm={12} md={4}>
                            <div className="radio">
                                <input type="radio" name="userType" value="client" style={{ position: "fixed", visibility: "hidden" }} />
                                <RadioButton active={formData.isClient}
                                    onClick={() => handleUserTypeSelection(true)}
                                >
                                    {singularClientName}
                                    <Circle active={formData.isClient}><CheckMark>&#x2713;</CheckMark></Circle>
                                </RadioButton>
                            </div>
                        </Grid>

                        <Grid item sm={12} md={4}>
                            &nbsp;
                        </Grid>
                    </Grid>
                    <StandardModalFormGrid columns="repeat(3, minmax(0, 1fr))">
                        <StandardModalFormGroup>
                            <StandardModalLabel>First Name <Required /></StandardModalLabel>
                            <StyledInput placeholder="First Name"
                                value={filterInputValue(formData.firstName)}
                                onChange={e => handleInputChange(e, "firstName")}
                                style={{ textTransform: "capitalize" }}
                            />
                        </StandardModalFormGroup>
                        <StandardModalFormGroup>
                            <StandardModalLabel>Last Name {!formData.isClient && <Required />}</StandardModalLabel>
                            <StyledInput required placeholder="Last Name"
                                value={filterInputValue(formData.lastName)}
                                onChange={e => handleInputChange(e, "lastName")}
                                style={{ textTransform: "capitalize" }}
                            />
                        </StandardModalFormGroup>
                        <StandardModalFormGroup>
                            <StandardModalLabel>Email <Required /></StandardModalLabel>
                            <StyledInput required placeholder="Email"
                                value={filterInputValue(formData.email)}
                                onChange={e => handleInputChange(e, "email")}
                            />
                        </StandardModalFormGroup>
                    </StandardModalFormGrid>
                    <StandardModalFormGrid columns="repeat(3, minmax(0, 1fr))">
                        <StandardModalFormGroup>
                            <StandardModalLabel>Mobile (Optional)</StandardModalLabel>

                            <PhoneInputWithDialCode
                                onChange={handlePhoneNumber}
                                onMount={onPhoneInputMounted}
                                countryCode={countryCode.toLowerCase()}
                                dialCode={dialCode}
                                value={filterInputValue(formData.phoneNumber)} />
                        </StandardModalFormGroup>

                        {!formData.isClient &&
                            <StandardModalFormGroup>
                                <StandardModalLabel>{formData.isClient ? `${pluralize.singular(customNames.clients)} Type` : "Department"} (Optional)</StandardModalLabel>
                                <StyledInput placeholder={formData.isClient ? `${pluralize.singular(customNames.clients)} Type` : "Department"}
                                    value={filterInputValue(formData.department)}
                                    onChange={e => handleInputChange(e, "department")}
                                />
                            </StandardModalFormGroup>}

                        <StandardModalFormGroup>
                            <StandardModalLabel>{formData.isClient ? `${pluralize.singular(customNames.clients)} Type (Optional)` : "Job Title (Optional)"}</StandardModalLabel>
                            <StyledInput placeholder={formData.isClient ? `${pluralize.singular(customNames.clients)} Type` : "Job Title"}
                                value={filterInputValue(formData.employmentPosition)}
                                onChange={e => handleInputChange(e, "employmentPosition")}
                            />
                        </StandardModalFormGroup>
                    </StandardModalFormGrid>
                    {!isFormCreate && (
                        <RequestDocumentsSection
                            documentTypes={documentTypes || []}
                            selectedDocuments={formData.selectedDocuments}
                            setSelectedDocuments={(updated) =>
                                setFormData(prev => ({
                                    ...prev,
                                    selectedDocuments: updated
                                }))
                            }
                        />
                    )}

                    <TagsAssignmentSection>
                        <TagsLeadText>
                            <StandardModalLabel>Assign Tags to Group Your Users</StandardModalLabel>
                            <StandardModalHelperText>
                                Tags let you create specific user groups in your Me Business user list.
                                Assign users to a tag to easily manage and select them across the app and business portal features.
                            </StandardModalHelperText>
                            <StandardModalHelperText>
                                For example, adding a user to the Marketing Team tag means they can be included whenever that tag is selected.
                            </StandardModalHelperText>
                        </TagsLeadText>
                        <TagsAssignmentBody>
                            <TagSelect
                                value=""
                                onChange={handleTagSelect}
                                disabled={availableTags.length === 0}
                            >
                                <option value="">
                                    {availableTags.length === 0 ? "No Tags Available" : "Select Tags"}
                                </option>
                                {availableTags.map(tag => (
                                    <option key={tag.uuid} value={tag.uuid}>
                                        {tag.name}
                                    </option>
                                ))}
                            </TagSelect>
                            <SelectedTagsBox>
                                {selectedTagObjects.length === 0 && (
                                    <EmptySelectedTags>No tags selected</EmptySelectedTags>
                                )}
                                {selectedTagObjects.map(tag => (
                                    <SelectedTagPill
                                        type="button"
                                        key={tag.uuid}
                                        onClick={() => removeSelectedTag(tag.uuid)}
                                    >
                                        <span>{tag.name}</span>
                                        <span>x</span>
                                    </SelectedTagPill>
                                ))}
                            </SelectedTagsBox>
                        </TagsAssignmentBody>
                    </TagsAssignmentSection>

                    {renderErrors}
                </UserFormContent>
            </StandardModal>
            <StandardModal
                open={pendingDiscardUserType !== null}
                onClose={handleCloseDiscardDialog}
                widthMode="small"
                hideCloseButton
                bodyPadding="0"
            >
                <DiscardDialogContent>
                    <DiscardTitle id="discard-text-edits-title">
                        Discard unsaved edits?
                    </DiscardTitle>
                    <DiscardMessage>
                        You have unsaved changes in this user profile. Switching user type will discard those changes before conversion.
                    </DiscardMessage>
                    <ConvertActions>
                        <StandardModalActionButton
                            width="140px"
                            onClick={handleCloseDiscardDialog}
                        >
                            Back
                        </StandardModalActionButton>
                        <StandardModalActionButton
                            $variant="primary"
                            width="140px"
                            onClick={handleConfirmDiscardTextEdits}
                        >
                            Continue
                        </StandardModalActionButton>
                    </ConvertActions>
                </DiscardDialogContent>
            </StandardModal>
            <StandardModal
                open={showConvertDialog}
                onClose={handleCloseConvertDialog}
                widthMode="large"
                hideCloseButton
                bodyPadding="0"
            >
                <ConvertDialogContent>
                    <img src={ConvertIcon} width="72px" height="72px" />
                    <ConvertTitle id="convert-user-type-title">
                        Convert {currentUserTypeName} to {nextUserTypeName}
                    </ConvertTitle>
                    <ConvertMessage>
                        Are you sure you want to convert this {currentUserTypeName.toLowerCase()} to a {nextUserTypeName.toLowerCase()}?
                        This action will <br/> remove the user from the {currentUserTypeName.toLowerCase()} list and move them to the {nextUserTypeName.toLowerCase()} list. <br/>
                        Please note: all permissions, data, and history associated with their <br/> employee status will be permanently lost.
                    </ConvertMessage>
                    <ConvertActions>
                        <StandardModalActionButton
                            width="140px"
                            onClick={handleCloseConvertDialog}
                            disabled={isLoadingConvert}
                        >
                            Back
                        </StandardModalActionButton>
                        <StandardModalActionButton
                            $variant="primary"
                            width="140px"
                            onClick={handleConfirmUserTypeChange}
                            disabled={isLoadingConvert}
                            style={{ opacity: isLoadingConvert && "0.6" }}
                        >
                            {isLoadingConvert ? "Confirming..." : "Confirm"}
                        </StandardModalActionButton>
                    </ConvertActions>
                </ConvertDialogContent>
            </StandardModal>
        </>
    );
};

export default UserDetailsForm;
